import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Clock, Calendar, ArrowRight, User } from 'lucide-react';
import { ArrowRight as ArrowRightP } from '@phosphor-icons/react';
import { blogPostsData } from '../data/blogPostsData';
import InquiryModal from '../components/InquiryModal';
import useEmblaCarousel from 'embla-carousel-react';

const Blogs = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [buttonContext, setButtonContext] = useState('');
  const handleButtonClick = (context) => { setButtonContext(context); setIsModalOpen(true); };
  const [emblaRef] = useEmblaCarousel({ loop: true, align: 'start' });

  return (
    <div className="min-h-screen pt-[72px] bg-[#FAFAFA]">
      <section className="bg-slate-900 py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#D4AF37] mb-4">Expert Insights</p>
          <h1 className="text-4xl sm:text-5xl font-heading font-semibold text-white tracking-tighter mb-6">
            Expert IP Strategies That<br /><span className="text-[#D4AF37]">Deliver Results</span>
          </h1>
          <p className="text-base text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Gain actionable insights and proven intellectual property strategies from real case studies to strengthen your IP portfolio and protect your brand effectively.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl font-heading font-medium text-slate-900 tracking-tight mb-2">Latest Articles</h2>
            <p className="text-sm text-slate-600">Expert insights on IP protection and business growth</p>
          </div>
          {/* Grid for desktop, slider for mobile */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-xs:hidden">
            {blogPostsData.map((post) => (
              <a href={`/blogs/${post.id}`} key={post.id} className="block">
                <Card className="group overflow-hidden border border-slate-200 hover:border-[#044B3B]/30 hover:shadow-lg bg-white h-full rounded-md transition-all duration-300">
                  <div className="relative h-52 overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute top-3 left-3">
                      <span className="inline-block px-3 py-1.5 bg-[#044B3B] text-white text-xs font-bold rounded-md uppercase tracking-wider">{post.category}</span>
                    </div>
                    {post.featured && <div className="absolute top-3 right-3"><span className="inline-block px-2 py-1 bg-[#D4AF37] text-white text-xs font-bold rounded-md uppercase">Featured</span></div>}
                  </div>
                  <div className="p-6 flex flex-col">
                    <h3 className="text-lg font-heading font-semibold text-slate-900 mb-3 leading-snug min-h-[52px] group-hover:text-[#044B3B] transition-colors">{post.title}</h3>
                    <p className="text-sm text-slate-500 mb-4 leading-relaxed line-clamp-3 flex-grow">{post.excerpt}</p>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {post.tags.slice(0, 2).map((tag, idx) => (<span key={idx} className="text-xs font-medium px-2 py-1 bg-slate-100 text-slate-600 rounded-md">{tag}</span>))}
                    </div>
                    <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-100">
                      <div className="w-8 h-8 bg-[#044B3B] rounded-md flex items-center justify-center flex-shrink-0"><User className="h-4 w-4 text-white" /></div>
                      <div><p className="font-semibold text-slate-900 text-xs">{post.author}</p><p className="text-xs text-slate-400">{post.role}</p></div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-slate-400">
                        <div className="flex items-center"><Clock className="h-3.5 w-3.5 mr-1" /><span>{post.readTime}</span></div>
                        <div className="flex items-center"><Calendar className="h-3.5 w-3.5 mr-1" /><span>{post.date}</span></div>
                      </div>
                      <div className="flex items-center gap-1.5 text-[#044B3B] font-semibold text-xs group-hover:gap-2.5 transition-all">Read More <ArrowRight className="h-3.5 w-3.5" /></div>
                    </div>
                  </div>
                </Card>
              </a>
            ))}
          </div>

          {/* Slider for mobile (576px and below) */}
          <div className="overflow-hidden xs:hidden" ref={emblaRef}>
            <div className="flex gap-4">
              {blogPostsData.map((post) => (
                <a href={`/blogs/${post.id}`} key={post.id} className="flex-[0_0_85%] block">
                  <Card className="group overflow-hidden border border-slate-200 hover:border-[#044B3B]/30 hover:shadow-lg bg-white h-full rounded-md transition-all duration-300">
                    <div className="relative h-52 overflow-hidden">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      <div className="absolute top-3 left-3">
                        <span className="inline-block px-3 py-1.5 bg-[#044B3B] text-white text-xs font-bold rounded-md uppercase tracking-wider">{post.category}</span>
                      </div>
                      {post.featured && <div className="absolute top-3 right-3"><span className="inline-block px-2 py-1 bg-[#D4AF37] text-white text-xs font-bold rounded-md uppercase">Featured</span></div>}
                    </div>
                    <div className="p-6 flex flex-col">
                      <h3 className="text-lg font-heading font-semibold text-slate-900 mb-3 leading-snug min-h-[52px] group-hover:text-[#044B3B] transition-colors">{post.title}</h3>
                      <p className="text-sm text-slate-500 mb-4 leading-relaxed line-clamp-3 flex-grow">{post.excerpt}</p>
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {post.tags.slice(0, 2).map((tag, idx) => (<span key={idx} className="text-xs font-medium px-2 py-1 bg-slate-100 text-slate-600 rounded-md">{tag}</span>))}
                      </div>
                      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-100">
                        <div className="w-8 h-8 bg-[#044B3B] rounded-md flex items-center justify-center flex-shrink-0"><User className="h-4 w-4 text-white" /></div>
                        <div><p className="font-semibold text-slate-900 text-xs">{post.author}</p><p className="text-xs text-slate-400">{post.role}</p></div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-xs text-slate-400">
                          <div className="flex items-center"><Clock className="h-3.5 w-3.5 mr-1" /><span>{post.readTime}</span></div>
                          <div className="flex items-center"><Calendar className="h-3.5 w-3.5 mr-1" /><span>{post.date}</span></div>
                        </div>
                        <div className="flex items-center gap-1.5 text-[#044B3B] font-semibold text-xs group-hover:gap-2.5 transition-all">Read More <ArrowRight className="h-3.5 w-3.5" /></div>
                      </div>
                    </div>
                  </Card>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#044B3B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-medium text-white tracking-tight mb-6">Protect Your Brand Before It's Too Late.</h2>
          <p className="text-base text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed">Secure your intellectual property and stop infringers from profiting off your work.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button onClick={() => handleButtonClick('Blogs CTA - Register')} className="bg-white text-[#044B3B] hover:bg-slate-100 font-semibold text-sm px-8 py-5 rounded-md">
              <span className="flex items-center gap-2">Start Registration <ArrowRightP className="h-4 w-4" /></span>
            </Button>
            <Button onClick={() => handleButtonClick('Blogs CTA - Consult')} className="bg-transparent border border-white/30 text-white hover:bg-white/10 font-semibold text-sm px-8 py-5 rounded-md">Schedule Consultation</Button>
          </div>
        </div>
      </section>

      <InquiryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} buttonContext={buttonContext} />
    </div>
  );
};

export default Blogs;
